import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  sparxsEditorBlueprintFetchedHydrated,
  sparxsEditorCatalogReplaced,
} from "@/features/sparxsEditor/store/domainActions";

export type SparxsBlueprintFetchFailureEntry = {
  lastErrorMessage?: string;
  /** HTTP 404 from blueprint GET — skip repeat network fetches until cleared. */
  notFound?: boolean;
};

export type SparxsBlueprintFetchFailuresState = {
  byFileName: Record<string, SparxsBlueprintFetchFailureEntry>;
};

const initialState: SparxsBlueprintFetchFailuresState = {
  byFileName: {},
};

const sparxsBlueprintFetchFailuresSlice = createSlice({
  name: "sparxsBlueprintFetchFailures",
  initialState,
  reducers: {
    recordSparxBlueprintFetchFailure: (
      state,
      action: PayloadAction<{
        fileName: string;
        lastErrorMessage?: string;
        notFound?: boolean;
      }>,
    ) => {
      const { fileName, lastErrorMessage, notFound } = action.payload;
      const prev = state.byFileName[fileName] ?? {};
      state.byFileName[fileName] = {
        ...prev,
        ...(lastErrorMessage != null && lastErrorMessage !== ""
          ? { lastErrorMessage }
          : {}),
        ...(notFound === true ? { notFound: true as const } : {}),
      };
    },
    clearSparxBlueprintFetchFailure: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      const { [key]: _removed, ...rest } = state.byFileName;
      state.byFileName = rest;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sparxsEditorBlueprintFetchedHydrated, (state, action) => {
        const { fileName } = action.payload;
        if (!(fileName in state.byFileName)) {
          return;
        }
        const { [fileName]: _removed, ...rest } = state.byFileName;
        state.byFileName = rest;
      })
      .addCase(sparxsEditorCatalogReplaced, (state, action) => {
        const keep = new Set(action.payload.files.map((f) => f.fileName));
        const next: Record<string, SparxsBlueprintFetchFailureEntry> = {};
        for (const name of keep) {
          const entry = state.byFileName[name];
          if (entry != null) {
            next[name] = entry;
          }
        }
        state.byFileName = next;
      });
  },
});

export const {
  recordSparxBlueprintFetchFailure,
  clearSparxBlueprintFetchFailure,
} = sparxsBlueprintFetchFailuresSlice.actions;

export default sparxsBlueprintFetchFailuresSlice.reducer;
